import { useEffect, useMemo, useState, useCallback } from "react";
import { Table, TableBody } from "@/components/atoms/Table";
import { FormHeader } from "@/components/molecules/FormHeader";
import { useTitle } from "@/core/context/TitleContext";
import { useFutureFlowsData } from "@/modules/dashboard/application/hooks/useFutureFlowsData";
import { FutureFlowsFilters } from "@/modules/dashboard/presentation/FutureFlowsFilters";
import { DashboardTableHeader } from "@/modules/dashboard/presentation/TableHeader";
import { TableMonthRow } from "@/modules/dashboard/presentation/TableMonthRow";
import { TableTotalRow } from "@/modules/dashboard/presentation/TableTotalRow";
import { FutureFlowEntity } from "@/modules/dashboard/domain/entities/DashboardEntity";

export const FutureFlowsPage = () => {
  const { years, futureFlow, instrumentsTypes, portfolioOptions } = useFutureFlowsData();
  const [portfolio, setPortfolio] = useState("");
  const [year, setYear] = useState("");
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);

  const { setTitle } = useTitle();

  useEffect(() => {
    setTitle("Dashboard");
  }, [setTitle]);

  // Filtrar flujos por portafolio y año
  const filteredFlow = useMemo(() => {
    if (!portfolio) return [];

    return futureFlow.filter(
      (ff) =>
        ff.portfolio.id === portfolio.toString() &&
        (year === "all" || ff.flowDate.getFullYear().toString() === year)
    );
  }, [portfolio, year, futureFlow]);

  // Agrupar por mes ("MM-mes-YYYY") e instrumento
  const groupedFlows = useMemo(() => {
    const grouped: Record<string, Record<string, number>> = {};
    for (const ff of filteredFlow) {
      const date = ff.flowDate;
      const monthIndex = date.getMonth();
      const monthName = date.toLocaleString("es-ES", { month: "long" });
      const monthYear = date.getFullYear();
      const key = `${String(monthIndex + 1).padStart(2, "0")}-${monthName}-${monthYear}`;
      const mnemonic = ff.instrument.mnemonic;
      const instrumentTypeId = ff.instrument.instrumentType;
      if (!grouped[key]) grouped[key] = {};
      if (!grouped[key][mnemonic]) grouped[key][mnemonic] = 0;
      if (!grouped[key][instrumentTypeId]) grouped[key][instrumentTypeId] = 0;
      grouped[key][instrumentTypeId] += ff.value;
    }
    return grouped;
  }, [filteredFlow]);

  // Memoizar detalles por mes para evitar recalcularlos en cada render
  const monthDetailsMap = useMemo(() => {
    const map: Record<string, FutureFlowEntity[]> = {};
    for (const ff of filteredFlow) {
      const date = ff.flowDate;
      const monthIndex = date.getMonth();
      const monthName = date.toLocaleString("es-ES", { month: "long" });
      const monthYear = date.getFullYear();
      const key = `${String(monthIndex + 1).padStart(2, "0")}-${monthName}-${monthYear}`;
      if (!map[key]) map[key] = [];
      map[key].push(ff);
    }

    for (const key in map) {
      map[key].sort((a, b) => a.flowDate.getTime() - b.flowDate.getTime());
    }

    return map;
  }, [filteredFlow]);

  const orderedMonthKeys = useMemo(() => {
    return Object.keys(groupedFlows).sort((a, b) => {
      const [monthA, , yearA] = a.split("-");
      const [monthB, , yearB] = b.split("-");
      const dateA = new Date(Number(yearA), Number(monthA) - 1);
      const dateB = new Date(Number(yearB), Number(monthB) - 1);
      return dateA.getTime() - dateB.getTime();
    });
  }, [groupedFlows]);

  const visibleHeaders = useMemo(() => {
    return ["Mes", "Año", ...instrumentsTypes?.map(it => it.name) ?? [], "Total Mes", "Acumulado"];
  }, [instrumentsTypes]);

  const totalColumns = visibleHeaders.length;

  const totalYear = useMemo(() => {
    return orderedMonthKeys.reduce((sumAll, monthKey) => {
      const flows = groupedFlows[monthKey];
      const monthSum = Object.values(flows).reduce((s, v) => s + v, 0);
      return sumAll + monthSum;
    }, 0);
  }, [orderedMonthKeys, groupedFlows]);

  const cumulativeMap = useMemo(() => {
    const map: Record<string, number> = {};
    let running = 0;
    for (const monthKey of orderedMonthKeys) {
      const flows = groupedFlows[monthKey];
      const monthSum = Object.values(flows).reduce((s, v) => s + v, 0);
      running += monthSum;
      map[monthKey] = running;
    }
    return map;
  }, [orderedMonthKeys, groupedFlows]);

  const handleToggleExpanded = useCallback((key: string) => {
    setExpandedMonth((prev) => (prev === key ? null : key));
  }, []);

  return (
    <>
      <FormHeader title="Flujos futuros" />

      <FutureFlowsFilters
        portfolio={portfolio}
        year={year}
        portfolioOptions={portfolioOptions}
        years={years}
        setPortfolio={setPortfolio}
        setYear={setYear}
      />

      <Table wrapperClassName="shadow-neumorph mt-5">
        <DashboardTableHeader headers={visibleHeaders} />

        <TableBody>
          {orderedMonthKeys.map((monthKey) => {
            const flowsByInstrument = groupedFlows[monthKey];
            const [, monthName, monthYear] = monthKey.split("-");
            const details = monthDetailsMap[monthKey] ?? [];

            const monthTotal = Object.values(flowsByInstrument).reduce(
              (sum, v) => sum + v,
              0
            );

            return (
              <TableMonthRow
                key={monthKey}
                monthKey={monthKey}
                flowsByInstrument={flowsByInstrument}
                monthName={monthName}
                monthYear={monthYear}
                monthTotal={monthTotal}
                monthAccum={cumulativeMap[monthKey]}
                instrumentsTypes={instrumentsTypes ?? []}
                isExpanded={expandedMonth === monthKey}
                toggleExpanded={handleToggleExpanded}
                details={details}
                totalYear={totalYear}
                totalColumns={totalColumns}
              />
            );
          })}

          <TableTotalRow
            instrumentsTypes={instrumentsTypes ?? []}
            groupedFlows={groupedFlows}
            orderedMonthKeys={orderedMonthKeys}
            totalYear={totalYear}
            totalColumns={totalColumns}
          />
        </TableBody>
      </Table>
    </>
  );
};
